import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Languages, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/store";
import { invalidateAliases } from "../../lib/voiceAliases";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

function AliasRow({ entry, auth, onSaved, onDeleted }) {
  const [text, setText] = useState((entry.aliases || []).join(", "));
  const [saving, setSaving] = useState(false);
  const dirty = text !== (entry.aliases || []).join(", ");

  useEffect(() => {
    setText((entry.aliases || []).join(", "));
  }, [entry.aliases]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/aliases`, {
        id: entry.id,
        canonical: entry.canonical,
        type: entry.type,
        aliases: text.split(",").map((s) => s.trim()).filter(Boolean),
      }, auth);
      invalidateAliases();
      onSaved();
      toast.success(`Aliases updated for "${entry.canonical}"`);
    } catch {
      toast.error("Could not save aliases");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the alias entry for "${entry.canonical}"?`)) return;
    try {
      await axios.delete(`${API}/admin/aliases/${entry.id}`, auth);
      invalidateAliases();
      onDeleted();
      toast.success("Entry deleted");
    } catch {
      toast.error("Could not delete the entry");
    }
  };

  return (
    <div className="border border-[#eee6d6] p-4" data-testid={`alias-row-${entry.canonical}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-[#171310] capitalize">
          {entry.canonical}
          <span className="ml-2 text-[9px] tracking-[0.15em] uppercase bg-[#f4eddc] text-[#8a6a1f] px-1.5 py-0.5">{entry.type === "world" ? "Collection" : "Product term"}</span>
        </p>
        <button onClick={remove} aria-label={`Delete ${entry.canonical} aliases`} className="text-[#8f8571] hover:text-[#8f2f2f] transition-colors p-1" data-testid={`alias-delete-${entry.canonical}`}>
          <Trash2 size={14} />
        </button>
      </div>
      <div className="flex gap-2 mt-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="kangan, kada, churi..." className="rounded-none border-[#d8cdb5] text-xs h-9" data-testid={`alias-input-${entry.canonical}`} />
        {dirty && (
          <Button onClick={save} disabled={saving} className="rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] text-[10px] uppercase tracking-wider h-9 px-4" data-testid={`alias-save-${entry.canonical}`}>
            {saving ? "..." : "Save"}
          </Button>
        )}
      </div>
    </div>
  );
}

export function AliasManagerCard({ auth }) {
  const [entries, setEntries] = useState(null);
  const [filter, setFilter] = useState("");
  const [newCanonical, setNewCanonical] = useState("");
  const [newType, setNewType] = useState("term");
  const [newAliases, setNewAliases] = useState("");
  const [adding, setAdding] = useState(false);
  const [misses, setMisses] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/admin/aliases`, auth)
      .then((r) => setEntries(r.data.aliases))
      .catch((e) => console.warn("aliases fetch failed:", e?.message));
    axios.get(`${API}/admin/voice-misses`, auth)
      .then((r) => setMisses(r.data.misses))
      .catch((e) => console.warn("misses fetch failed:", e?.message));
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newCanonical.trim()) return toast.error("Enter the catalog term this maps to (e.g. bangles)");
    setAdding(true);
    try {
      await axios.post(`${API}/admin/aliases`, {
        canonical: newCanonical.trim(),
        type: newType,
        aliases: newAliases.split(",").map((s) => s.trim()).filter(Boolean),
      }, auth);
      invalidateAliases();
      setNewCanonical("");
      setNewAliases("");
      load();
      toast.success("Alias entry added");
    } catch (e) {
      toast.error(e?.response?.status === 409 ? e.response.data.detail : "Could not add the entry");
    } finally {
      setAdding(false);
    }
  };

  const applyMiss = (m) => {
    setNewAliases((prev) => (prev ? `${prev}, ${m.phrase}` : m.phrase));
    toast.info(`"${m.phrase}" added to the new-entry aliases — pick the catalog term and press Add`);
  };

  const dismissMiss = async (m) => {
    try {
      await axios.delete(`${API}/admin/voice-misses/${m.id}`, auth);
      setMisses((list) => list.filter((x) => x.id !== m.id));
    } catch {
      toast.error("Could not dismiss");
    }
  };

  const shown = (entries || []).filter((e) =>
    !filter || e.canonical.includes(filter.toLowerCase()) || (e.aliases || []).some((a) => a.includes(filter.toLowerCase()))
  );

  return (
    <div className="bg-white border border-[#eee6d6] p-6" data-testid="alias-manager-card">
      <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
        <Languages size={18} className="text-[#a8842c]" /> Voice Search Aliases
      </h3>
      <p className="text-sm text-[#8f8571] mt-1">
        Teach “Hey Regal” the words your community uses — kangan, haar, jhumka, churi and more. Each catalog term can have
        any number of spoken names in English, Punjabi, Hindi or Urdu. Comma-separate the aliases.
      </p>

      <div className="border border-[#eee6d6] bg-[#faf7f0] p-4 mt-5">
        <Label className="text-[11px] uppercase tracking-wider text-[#7a7160]">Add a new entry</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          <Input value={newCanonical} onChange={(e) => setNewCanonical(e.target.value)} placeholder="Catalog term (e.g. bangles)" className="rounded-none border-[#d8cdb5] h-9 text-xs flex-1 min-w-[150px]" data-testid="alias-new-canonical" />
          <select value={newType} onChange={(e) => setNewType(e.target.value)} className="border border-[#d8cdb5] h-9 text-xs px-2 bg-white" data-testid="alias-new-type">
            <option value="term">Product term</option>
            <option value="world">Collection / world</option>
          </select>
          <Input value={newAliases} onChange={(e) => setNewAliases(e.target.value)} placeholder="Aliases: kangan, kada, churi" className="rounded-none border-[#d8cdb5] h-9 text-xs flex-1 min-w-[180px]" data-testid="alias-new-aliases" />
          <Button onClick={add} disabled={adding} className="rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] text-[10px] uppercase tracking-wider h-9 px-4" data-testid="alias-add-btn">
            <Plus size={12} className="mr-1" /> Add
          </Button>
        </div>
      </div>

      {misses.length > 0 && (
        <div className="border border-[#e6d3a8] bg-[#fdf9ef] p-4 mt-4" data-testid="voice-misses-section">
          <Label className="text-[11px] uppercase tracking-wider text-[#8a6a1f]">Heard but not matched — real customer words ({misses.length})</Label>
          <p className="text-[11px] text-[#8f8571] mt-1">Voice phrases that found no products. Tap “Use” to turn one into an alias.</p>
          <div className="space-y-1.5 mt-3 max-h-[220px] overflow-y-auto pr-1">
            {misses.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-2 bg-white border border-[#eee6d6] px-3 py-2" data-testid={`voice-miss-row-${m.id}`}>
                <p className="text-xs text-[#171310] min-w-0 truncate">
                  “{m.phrase}”
                  <span className="ml-2 text-[10px] text-[#8f8571]">{m.lang?.split("-")[0]} · heard {m.count}× · {m.lastAt ? new Date(m.lastAt).toLocaleDateString() : ""}</span>
                </p>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => applyMiss(m)} className="text-[10px] uppercase tracking-wider border border-[#c9a24b] text-[#8a6a1f] px-2 py-1 hover:bg-[#c9a24b]/15 transition-colors" data-testid={`voice-miss-use-${m.id}`}>Use</button>
                  <button onClick={() => dismissMiss(m)} aria-label="Dismiss phrase" className="text-[#8f8571] hover:text-[#8f2f2f] px-1 transition-colors" data-testid={`voice-miss-dismiss-${m.id}`}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter — try 'kangan' or 'earrings'..." className="rounded-none border-[#d8cdb5] h-9 text-xs mt-4" data-testid="alias-filter" />

      {!entries ? (
        <p className="text-sm text-[#8f8571] mt-4">Loading...</p>
      ) : (
        <div className="grid lg:grid-cols-2 gap-3 mt-4 max-h-[520px] overflow-y-auto pr-1" data-testid="alias-list">
          {shown.map((e) => <AliasRow key={e.id} entry={e} auth={auth} onSaved={load} onDeleted={load} />)}
          {shown.length === 0 && <p className="text-sm text-[#8f8571]">No entries match.</p>}
        </div>
      )}
    </div>
  );
}
